<script lang="ts" setup>
  import { useShared, useTranslator } from '@othercommerce/foundation';
  import { computed } from 'vue';

  const shared = useShared();
  const translator = useTranslator();

  const name = computed(() => shared.value.appName || 'Promoznawcy');
  const ariaLabel = computed(() => translator.trans('SHOP.COMMON.ARIA.COMPANY_LOGO_EXTENDED', { name: name.value }));

  const department = computed(() => {
    if (shared.value.department) {
      return shared.value.department;
    }
  });
</script>
<template>
  <footer id="footer" class="footer">
    <div class="container footer__content">
      <div v-if="department" class="footer__block footer__block--contact">
        <SfLogo class="footer__logo" role="img" :aria-label="ariaLabel" />
        <address class="footer__contact-content">
          <a v-if="department.phone" :href="`tel:${department.phone}`" class="footer__phone">
            <SfIconPhone aria-hidden="true" />
            <span v-text="department.phone" />
          </a>
          <a v-if="department.email" :href="`mailto:${department.email}`" class="footer__mail">
            <SfIconEnvelope aria-hidden="true" />
            <span v-text="department.email" />
          </a>
          <a :href="department.maps || '#'" class="footer__address" target="_blank">
            <SfIconGeoAlt aria-hidden="true" />
            <span class="footer__address-content">
              <span v-if="department.firstLine" v-text="department.firstLine" />
              <span v-if="department.secondLine" v-text="department.secondLine" />
              <span v-if="department.thirdLine" v-text="department.thirdLine" />
            </span>
          </a>
        </address>
        <FooterSocialsMedia />
      </div>
      <Collapsible v-for="group in shared.menu.menu_bottom">
        <template #header>
          <SfHeading type="span" v-if="group.name" :id="group.name">{{ group.name }}</SfHeading>
        </template>
        <template #content>
          <nav class="footer__block footer__block--categories" :aria-labelledby="group.name">
            <ul>
              <li v-for="item in group.children">
                <FooterNavItem :item="item" class="link" />
              </li>
            </ul>
          </nav>
        </template>
      </Collapsible>
      <FooterPaymentPartners class="footer__payment-partner" />
    </div>
    <span class="footer__separator" />
    <div class="container footer__bottom">
      <CopyrightMessage />
      <p>
        {{ $t('SHOP.COMMON.LABEL.REALIZATION') }}<a class="bottom-link" href="https://promoznawcy.pl/" target="_blank">PROMOznawcy.pl</a>
      </p>
    </div>
    <FixedActions />
  </footer>
</template>
<style lang="scss" scoped>
  .footer {
    display: flex;
    flex-direction: column;
    margin-top: 4rem;
    box-shadow: var(--secondary-box-shadow-200);
    background-color: var(--color-white);

    &__content {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 2rem;
      padding: 4rem 0 3rem;
      font-size: 0.875rem;

      ul {
        list-style: none;
      }

      a {
        padding: 0.25rem 0;
      }
    }

    &__address-content,
    &__block,
    &__contact-content,
    &__hours {
      display: flex;
      flex-direction: column;
    }

    &__block {
      gap: 1rem;

      ul {
        display: flex;
        flex-direction: column;

        li {
          display: inline-flex;
        }
      }
    }

    &__contact-content {
      gap: 0.5rem;
    }

    &__logo {
      width: 200px;
      height: auto;
    }

    &__phone,
    &__mail,
    &__address {
      font-size: 1rem;
      display: inline-flex;
      align-items: flex-start;
      gap: 0.5rem;

      svg {
        font-size: 1.25rem;
      }
    }

    &__payment-partner {
      grid-column: 1 / -1;
    }

    &__separator {
      display: block;
      width: 100%;
      height: 1px;
      background-color: var(--color-grey);
    }

    &__hours {
      gap: 0.125rem;
    }

    &__day {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;

      b {
        font-weight: var(--font-weight-semi);

        &::after {
          content: ":";
        }
      }
    }

    a {
      text-decoration: underline;
      text-decoration-color: transparent;
      transition: text-decoration-color 0.2s ease-out;
    }

    @media(hover: hover) {
      a:hover {
        text-decoration-color: var(--primary-color);
      }
    }

    &__bottom {
      display: flex;
      justify-content: space-between;
      padding: 1rem 0;

      p {
        font-size: 0.875rem;
      }
    }

    @media(max-width: 992px) {
      margin-top: 2rem;

      &__block--contact {
        margin-bottom: 1rem;
      }

      &__content {
        padding: 3rem 1rem 1rem;
        grid-template-columns: 1fr;
        gap: 0;
      }

      &__payment-partner {
        padding: 2rem 0 1rem;
      }

      &__bottom {
        flex-direction: column;
        align-items: center;
        padding: 1rem 0.625rem;

        p {
          text-align: center;
        }
      }

      &__block {
        gap: 0.5rem;
        width: 100%;
      }

      .heading {
        flex: none;
      }

      &__phone,
      &__mail,
      &__address {
        font-size: 0.875rem;

        svg {
          font-size: 1.125rem;
        }
      }
    }
  }
</style>
